import {
  DEFAULT_CHOICE_OPTIONS,
  DEFAULT_RATING_CONFIG,
  DEFAULT_SLIDER_CONFIG,
  type HumanInputComponentType,
} from "@/components/PowerBlock/Blocks/categories/data-sources/HumanInputBlock/constants";
import type {
  ChoiceUIElement,
  HumanConfiguration,
  RatingUIElement,
  SliderUIElement,
} from "@/types/evaluate";

export enum HumanUIComponentType {
  TEXT = "text",
  SLIDER = "slider",
  RATING = "rating",
  CHOICE = "choice",
}

type SmartTableHumanUIComponent = Exclude<HumanInputComponentType, "text">;

export const getHumanConfigurationForComponent = (
  component: SmartTableHumanUIComponent,
  current?: HumanConfiguration,
): HumanConfiguration => {
  switch (component) {
    case "rating":
      return {
        data_type: "number",
        ui_element: {
          component,
          max:
            current?.ui_element.component === component
              ? current.ui_element.max
              : DEFAULT_RATING_CONFIG.max,
        },
      };
    case "choice":
      return {
        data_type: "string",
        ui_element: {
          component,
          options:
            current?.ui_element.component === component
              ? current.ui_element.options
              : DEFAULT_CHOICE_OPTIONS,
        },
      };
    case "slider":
      return {
        data_type: "number",
        ui_element: {
          component,
          min:
            current?.ui_element.component === component
              ? current.ui_element.min
              : DEFAULT_SLIDER_CONFIG.min,
          max:
            current?.ui_element.component === component
              ? current.ui_element.max
              : DEFAULT_SLIDER_CONFIG.max,
        },
      };
    default:
      throw new Error(`Unsupported human input component: ${component}`);
  }
};

export const createDefaultHumanSliderConfiguration = (): HumanConfiguration =>
  getHumanConfigurationForComponent(HumanUIComponentType.SLIDER);

export const createDefaultHumanRatingConfiguration = (): HumanConfiguration =>
  getHumanConfigurationForComponent(HumanUIComponentType.RATING);

export const createDefaultHumanChoiceConfiguration = (): HumanConfiguration =>
  getHumanConfigurationForComponent(HumanUIComponentType.CHOICE);

export const resolveHumanInputConfiguration = (
  configuration: Record<string, unknown> | null | undefined,
): HumanConfiguration => {
  const uiElement = configuration?.ui_element;

  if (configuration?.data_type === "number" && uiElement && typeof uiElement === "object") {
    if ("component" in uiElement && uiElement.component === HumanUIComponentType.SLIDER) {
      return {
        data_type: "number",
        ui_element: uiElement as SliderUIElement,
      };
    }

    if ("component" in uiElement && uiElement.component === HumanUIComponentType.RATING) {
      return {
        data_type: "number",
        ui_element: uiElement as RatingUIElement,
      };
    }
  }

  if (
    configuration?.data_type === "string" &&
    uiElement &&
    typeof uiElement === "object" &&
    "component" in uiElement &&
    uiElement.component === HumanUIComponentType.CHOICE
  ) {
    return {
      data_type: "string",
      ui_element: uiElement as ChoiceUIElement,
    };
  }

  return createDefaultHumanSliderConfiguration();
};

export const getHumanInputDisplayValue = (
  uiComponent: string | undefined,
  value: string | number | undefined | null,
  config?: HumanConfiguration,
): string => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  switch (uiComponent) {
    case HumanUIComponentType.SLIDER: {
      const sliderConfig = config?.ui_element as SliderUIElement;
      return `${value} / ${sliderConfig.max}`;
    }
    case HumanUIComponentType.RATING: {
      const ratingConfig = config?.ui_element as RatingUIElement;
      return `${value} / ${ratingConfig.max}`;
    }
    case HumanUIComponentType.CHOICE:
    case HumanUIComponentType.TEXT:
    default:
      return String(value);
  }
};

export const getSliderBounds = (config: HumanConfiguration) => {
  const sliderConfig = config.ui_element as SliderUIElement;

  return {
    min: sliderConfig.min ?? DEFAULT_SLIDER_CONFIG.min,
    max: sliderConfig.max ?? DEFAULT_SLIDER_CONFIG.max,
  };
};

export const getRatingMax = (config: HumanConfiguration): number => {
  const ratingConfig = config.ui_element as RatingUIElement;
  return ratingConfig.max ?? DEFAULT_RATING_CONFIG.max;
};

export const getChoiceOptions = (config: HumanConfiguration): string[] => {
  const choiceConfig = config.ui_element as ChoiceUIElement;
  return Array.isArray(choiceConfig.options) && choiceConfig.options.length > 0
    ? choiceConfig.options
    : DEFAULT_CHOICE_OPTIONS;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getHumanInputResultSchemaSignature = (
  config: Record<string, unknown> | null | undefined,
): string => {
  if (!isRecord(config)) {
    return JSON.stringify(["string", HumanUIComponentType.TEXT]);
  }

  const dataType =
    config.data_type === "number" || config.data_type === "string" ? config.data_type : "string";
  const uiElement = config.ui_element;
  if (!isRecord(uiElement)) {
    return JSON.stringify([dataType, null]);
  }

  const { component } = uiElement;
  if (component === HumanUIComponentType.SLIDER) {
    return JSON.stringify([dataType, component, uiElement.min, uiElement.max]);
  }

  if (component === HumanUIComponentType.RATING) {
    return JSON.stringify([dataType, component, uiElement.max]);
  }

  if (component === HumanUIComponentType.CHOICE) {
    const options = Array.isArray(uiElement.options)
      ? uiElement.options.filter((option): option is string => typeof option === "string")
      : [];
    return JSON.stringify([dataType, component, options]);
  }

  return JSON.stringify([dataType, component]);
};

export const didHumanInputResultSchemaChange = (
  previousConfig: Record<string, unknown> | null | undefined,
  nextConfig: Record<string, unknown> | null | undefined,
): boolean =>
  getHumanInputResultSchemaSignature(previousConfig) !==
  getHumanInputResultSchemaSignature(nextConfig);

export const getSliderInputValue = (
  config: HumanConfiguration,
  cellValue: string | number | undefined | null,
) => {
  if (cellValue !== null && cellValue !== undefined && cellValue !== "") {
    return cellValue.toString();
  }

  const { min } = getSliderBounds(config);
  return min.toString();
};

export const unwrapHumanCellValue = (value: unknown): string | number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "object" && value !== null && "value" in value) {
    const nested = (value as { value: unknown }).value;
    if (typeof nested === "string" || typeof nested === "number") {
      return nested;
    }
    if (nested === null || nested === undefined) {
      return null;
    }
    return String(nested);
  }

  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  return String(value);
};
